// ═══════════════════════════════════════════════════════════════════════════
// PriceListPreview
// Extraído VERBATIM de index.html v0.9.16.
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { fmt } from "../pdf/comunes.js";
import { generatePriceListPDF } from "../pdf/lista-precios.js";
import { generarCuadernilloPDF } from "../pdf/cuadernillo.js";
import { LOGO_BASE64 } from "../pdf/logo.js";

/**
 * ¿Este producto tiene con qué llenar una página de cuadernillo?
 *
 * Basta la ficha del vino: una vinícola que no use el concepto trilateral
 * tiene páginas legítimas con foto, descripción, cata y maridaje. Lo que no se
 * puede es imprimir una página con solo el nombre.
 */
const tieneFicha = (p) =>
  !!(p.description || p.tastingNotes || p.pairing || p.labelStory ||
     p.workTitle || p.artistName);

export function PriceListPreview({ client, products, getPrice, discount, onClose, vinicola }) {
  const coral = "#e0563f";
  const coralBg = "#fdeee9";
  const ivaRate = client.ivaRate || 16;
  const activeProducts = products.filter(p => p.category !== "cortesia" && p.grossPrice > 0);
  const categories = [...new Set(activeProducts.map(p => p.category))];
  const catLabels = { tinto: "Vinos Tintos", blanco: "Vinos Blancos", naranja: "Vino Naranja", vermu: "Vermús", cerveza: "Cervezas", destilado: "Destilados", otro: "Otros" };

  const allIds = activeProducts.map(p => p.id);
  const [selectedIds, setSelectedIds] = useState(() => new Set(allIds));
  const allSelected = allIds.length > 0 && allIds.every(id => selectedIds.has(id));
  const selectedCount = allIds.filter(id => selectedIds.has(id)).length;
  const toggle = (id) => setSelectedIds(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleAll = () => setSelectedIds(allSelected ? new Set() : new Set(allIds));
  const conFicha = activeProducts.filter(p => selectedIds.has(p.id) && tieneFicha(p)).length;

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", overflow: "auto", padding: 20 }}
      onClick={onClose}>
      {/* `clamp` en el padding: 48px de holgura a cada lado están bien en una
          pantalla de escritorio y se comen un tercio de un iPhone de 375. */}
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", color: "#111", maxWidth: 800, width: "100%", borderRadius: 8, padding: "clamp(20px, 5vw, 40px) clamp(14px, 6vw, 48px)", marginTop: 20, marginBottom: 40, fontFamily: "'DM Sans', sans-serif", fontSize: 12 }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24 }}>
          <img src={LOGO_BASE64} alt="Vinícola Total" style={{ width: 64, height: 64, objectFit: "contain" }} />
          <div>
            <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, margin: 0, color: coral }}>LISTA DE PRECIOS</h1>
            <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>{new Date().toLocaleDateString("es-MX", { year: "numeric", month: "long", day: "numeric" })}</div>
          </div>
        </div>

        {/* Client info */}
        <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: "2px solid #eee" }}>
          <div style={{ fontSize: 10, color: coral, letterSpacing: 2, marginBottom: 6, fontWeight: 600 }}>PREPARADA PARA</div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>{client.name}</div>
          {discount > 0 && <div style={{ fontSize: 13, color: "#4a8a4a", marginTop: 4 }}>Descuento especial: {discount}%</div>}
        </div>

        {/* Selection bar */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, padding: "10px 14px", background: coralBg, borderRadius: 8, flexWrap: "wrap", gap: 8 }}>
          <div style={{ fontSize: 12, color: "#7a4a3f" }}>
            Marca los productos que quieres incluir · <b>{selectedCount}</b> de {allIds.length} seleccionados
          </div>
          <button onClick={toggleAll}
            style={{ padding: "6px 14px", border: "1px solid " + coral, borderRadius: 6, background: "#fff", cursor: "pointer", fontSize: 12, color: coral, fontWeight: 600 }}>
            {allSelected ? "Quitar todos" : "Seleccionar todos"}
          </button>
        </div>

        {/* Products by category */}
        {categories.map(cat => {
          const catProds = activeProducts.filter(p => p.category === cat);
          if (catProds.length === 0) return null;
          return (
            <div key={cat} style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: coral, borderBottom: "2px solid " + coral, paddingBottom: 4, marginBottom: 12, fontFamily: "'Cormorant Garamond', serif" }}>
                {catLabels[cat] || cat}
              </div>
              {catProds.map(p => {
                const checked = selectedIds.has(p.id);
                const price = getPrice(p.id);
                const ieps = p.iepsRate || 26.5;
                const netPrice = price * (1 + ieps / 100) * (1 + ivaRate / 100);
                const discountedNet = netPrice * (1 - discount / 100);
                return (
                  <div key={p.id} onClick={() => toggle(p.id)}
                    style={{ display: "flex", gap: 14, padding: "12px 0", borderBottom: "1px solid #f0f0f0", alignItems: "flex-start", cursor: "pointer", opacity: checked ? 1 : 0.4, transition: "opacity .15s" }}>
                    <input type="checkbox" checked={checked} onChange={() => toggle(p.id)} onClick={e => e.stopPropagation()}
                      style={{ marginTop: 4, width: 18, height: 18, accentColor: coral, cursor: "pointer", flexShrink: 0 }} />
                    {/* `contain`: la botella entera, no un recorte. Aquí importa
                        más que en Productos, porque esto lo ve el cliente. */}
                    {p.imageBase64 ? (
                      <img src={p.imageBase64} alt="" style={{ width: 60, height: 80, objectFit: "contain", borderRadius: 6, border: "1px solid #eee", background: "#fff", flexShrink: 0 }} />
                    ) : (
                      <div style={{ width: 60, height: 80, background: coralBg, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 24 }}>
                        {cat === "tinto" ? "🍷" : cat === "blanco" ? "🥂" : cat === "cerveza" ? "🍺" : "🍸"}
                      </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{p.name}</div>
                      {p.description && <div style={{ fontSize: 11, color: "#666", lineHeight: 1.5, marginBottom: 4 }}>{p.description}</div>}
                      {p.tastingNotes && <div style={{ fontSize: 11, color: "#9a6a5a", fontStyle: "italic", lineHeight: 1.5 }}>Notas de cata: {p.tastingNotes}</div>}
                      <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {(p.stock || 0) > 0
                          ? <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, background: "#e8f8e8", color: "#2a7a2a" }}>{p.stock} disponibles</span>
                          : <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, background: "#f8e8e8", color: "#7a2a2a" }}>Agotado</span>
                        }
                        {/* Sin esto, el vendedor marca ocho vinos, pide el
                            cuadernillo y le salen cinco páginas sin saber por
                            qué faltaron tres. */}
                        {!tieneFicha(p) && (
                          <span title="Este producto no tiene descripción, cata, maridaje ni obra: no entra al cuadernillo."
                            style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, background: "#f4f1e8", color: "#8a7a4a" }}>
                            sin ficha
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0, minWidth: 100 }}>
                      <div style={{ fontSize: 18, fontWeight: 700, color: coral }}>{fmt(discountedNet)}</div>
                      {discount > 0 && <div style={{ fontSize: 11, color: "#999", textDecoration: "line-through" }}>{fmt(netPrice)}</div>}
                      <div style={{ fontSize: 9, color: "#aaa", marginTop: 2 }}>IVA {ivaRate}% + IEPS incluidos</div>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}

        {/* Actions */}
        {/* ── Que quepan en un teléfono ──────────────────────────────────
            Esta barra tenía dos botones y funcionaba. Al agregar el del
            cuadernillo pasó a pedir unos 470px, y en un iPhone de 375 —con el
            modal descontando su padding— el último quedaba fuera de la
            pantalla: no se veía sin hacer pinch y arrastrar.
            Faltaba `flexWrap`, que es lo que convierte "se sale" en "baja al
            renglón siguiente". */}
        <div style={{ display: "flex", justifyContent: "flex-end", flexWrap: "wrap", gap: 10, marginTop: 24, paddingTop: 16, borderTop: "2px solid #eee" }}>
          <button onClick={onClose}
            style={{ padding: "12px 20px", border: "1px solid #ddd", borderRadius: 6, background: "#fff", cursor: "pointer", fontSize: 13, color: "#666", flex: "0 1 auto" }}>
            Cerrar
          </button>
          {/* ── Dos documentos, no uno ────────────────────────────────────
              La lista sirve para cotizar y el cuadernillo para convencer. El
              vendedor elige de la misma selección y se lleva el que necesite,
              o los dos. Por eso el cuadernillo NO reemplazó a la lista. */}
          <button disabled={conFicha === 0}
            title={conFicha === 0
              ? "Ninguno de los productos seleccionados tiene ficha que imprimir"
              : "Una página por producto, con su ficha. Sin precios."}
            onClick={() => { generarCuadernilloPDF(vinicola || {}, products.filter(p => selectedIds.has(p.id) && tieneFicha(p))); }}
            style={{ padding: "12px 20px", border: "1px solid " + coral, borderRadius: 6, background: "#fff", color: conFicha === 0 ? "#bbb" : coral, borderColor: conFicha === 0 ? "#ddd" : coral, cursor: conFicha === 0 ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 600, flex: "1 1 auto", minWidth: 150 }}>
            📖 Cuadernillo{conFicha > 0 ? " (" + conFicha + ")" : ""}
          </button>
          <button disabled={selectedCount === 0} onClick={() => { generatePriceListPDF(client, products.filter(p => selectedIds.has(p.id)), getPrice, discount); }}
            style={{ padding: "12px 20px", border: "none", borderRadius: 6, background: selectedCount === 0 ? "#ccc" : "linear-gradient(135deg, #d8482f, #e0563f)", color: "#fff", cursor: selectedCount === 0 ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 600, flex: "1 1 auto", minWidth: 150 }}>
            📥 Lista de precios{selectedCount > 0 ? " (" + selectedCount + ")" : ""}
          </button>
        </div>
        {/* El número del cuadernillo puede ser menor que el de la lista, y eso
            confundiría sin decir por qué. Se dice. */}
        {selectedCount > conFicha && (
          <div style={{ fontSize: 11, color: "#999", textAlign: "right", marginTop: 8 }}>
            {selectedCount - conFicha} de los seleccionados no tienen ficha todavía
            y no entran al cuadernillo.
          </div>
        )}
      </div>
    </div>
  );
}
